import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatDate, getStatusColor, generateWhatsAppLink } from '../utils/helpers';
import { 
  Plus, WhatsappLogo, Trash, Lock, Crown, Gift, Lightning, 
  Check, Users, Receipt, ChartLine, Wallet, Star, Clock
} from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const FREE_LEADS_LIMIT = 10;

export const LeadsPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  const [leadForm, setLeadForm] = useState({
    customer_name: '',
    mobile: '',
    email: '',
    message: '',
    source: 'manual',
  });

  const benefits = [
    { icon: Users, title: 'Unlimited Enquiry', desc: 'सर्व enquiry track करा' },
    { icon: Receipt, title: 'Professional Invoices', desc: 'WhatsApp वर share करा' },
    { icon: Wallet, title: 'Payment Tracking', desc: 'Pending payments track करा' },
    { icon: ChartLine, title: 'Business Analytics', desc: 'Revenue analytics' },
    { icon: WhatsappLogo, title: 'WhatsApp Integration', desc: 'Direct messaging' },
    { icon: Star, title: 'Remove Branding', desc: 'Profile वरून branding हटवा' },
  ];

  useEffect(() => {
    fetchLeads();
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPremium(response.data.is_active);
      localStorage.setItem('is_premium', response.data.is_active ? 'true' : 'false');
    } catch (error) {
      setIsPremium(localStorage.getItem('is_premium') === 'true');
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    
    // Check if free limit reached
    if (!isPremium && leads.length >= FREE_LEADS_LIMIT) {
      toast.error(`Free plan मध्ये फक्त ${FREE_LEADS_LIMIT} leads add करता येतात. Premium घ्या!`);
      return;
    }
    
    try {
      const designerId = JSON.parse(atob(localStorage.getItem('token').split('.')[1])).designer_id;
      await leadsAPI.create({ ...leadForm, designer_id: designerId });
      toast.success('Enquiry added successfully');
      setDialogOpen(false);
      setLeadForm({ customer_name: '', mobile: '', email: '', message: '', source: 'manual' });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to add lead');
    }
  };

  const handleUpdateStatus = async (leadId, status, notes) => {
    try {
      await leadsAPI.update(leadId, { status, notes });
      toast.success('Enquiry updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await leadsAPI.delete(id);
      toast.success('Enquiry deleted');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const contactViaWhatsApp = (lead) => {
    const message = `Hi ${lead.customer_name}, thank you for contacting us!`;
    window.open(generateWhatsAppLink(lead.mobile, message), '_blank');
  };

  // Determine which leads to show (first 10 are free)
  const visibleLeads = isPremium ? leads : leads.slice(0, FREE_LEADS_LIMIT);
  const lockedLeadsCount = isPremium ? 0 : Math.max(0, leads.length - FREE_LEADS_LIMIT);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="leads-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
              Enquiry
            </h1>
            <p className="text-[#6B7280] font-medium leading-relaxed">
              {leads.length} total enquiries 
              {!isPremium && <span className="text-orange-600 ml-2">({FREE_LEADS_LIMIT} FREE)</span>}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-lead-button" 
                className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full"
                disabled={!isPremium && leads.length >= FREE_LEADS_LIMIT}
              >
                <Plus className="h-4 w-4 mr-2" weight="bold" /> New Enquiry
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-lead-dialog">
              <DialogHeader>
                <DialogTitle>Add New Enquiry</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddLead} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Customer Name</Label>
                  <Input id="customer_name" data-testid="lead-name-input" value={leadForm.customer_name} onChange={(e) => setLeadForm({ ...leadForm, customer_name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input id="mobile" data-testid="lead-mobile-input" value={leadForm.mobile} onChange={(e) => setLeadForm({ ...leadForm, mobile: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input id="email" data-testid="lead-email-input" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" data-testid="lead-message-input" value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })} placeholder="Requirement details..." />
                </div>
                <Button type="submit" data-testid="submit-lead-button" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">Add Enquiry</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Free Limit Banner */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Gift className="h-5 w-5 text-orange-600" weight="duotone" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Free Plan: {leads.length}/{FREE_LEADS_LIMIT} Enquiry Used</p>
                  <p className="text-sm text-gray-600">Unlimited enquiry साठी Premium घ्या</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/premium')}
                className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full text-sm"
              >
                <Crown className="h-4 w-4 mr-1" weight="fill" /> Upgrade
              </Button>
            </div>
          </div>
        )}

        {/* Enquiry List */}
        <div className="space-y-4">
          {leads.length === 0 ? (
            <Card className="border-[#E5E7EB] bg-white">
              <CardContent className="pt-6">
                <p className="text-center text-[#6B7280]">No enquiry yet. Add your first enquiry to get started!</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {visibleLeads.map((lead, index) => (
                <Card key={lead.id} data-testid={`lead-item-${lead.id}`} className="border-[#E5E7EB] bg-white hover:border-[#FF5A36] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-['Chivo'] text-lg font-bold text-[#121212]">{lead.customer_name}</h3>
                              {!isPremium && index < FREE_LEADS_LIMIT && (
                                <Badge className="bg-green-100 text-green-700 border-0 text-xs">FREE</Badge>
                              )}
                            </div>
                            <p className="text-sm text-[#6B7280]">{lead.mobile}</p>
                            {lead.email && <p className="text-sm text-[#6B7280]">{lead.email}</p>}
                          </div>
                        </div>
                        {lead.message && (
                          <p className="text-sm text-[#121212] mb-3 bg-[#FAFAFA] p-3 rounded">{lead.message}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                          <span>Source: {lead.source}</span>
                          <span>•</span>
                          <span>{formatDate(lead.created_at)}</span>
                        </div>
                        {lead.notes && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-[#6B7280] mb-1">Notes:</p>
                            <p className="text-sm text-[#121212]">{lead.notes}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Badge className={getStatusColor(lead.status)} data-testid={`lead-status-${lead.id}`}>
                          {lead.status}
                        </Badge>
                        <Select
                          value={lead.status}
                          onValueChange={(status) => handleUpdateStatus(lead.id, status, lead.notes)}
                        >
                          <SelectTrigger data-testid={`lead-status-select-${lead.id}`} className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="quoted">Quoted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          data-testid={`whatsapp-lead-${lead.id}`}
                          onClick={() => contactViaWhatsApp(lead)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <WhatsappLogo className="h-4 w-4 mr-1" weight="fill" /> Contact
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          data-testid={`delete-lead-${lead.id}`}
                          onClick={() => handleDelete(lead.id)}
                        >
                          <Trash className="h-4 w-4" weight="bold" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Locked Enquiry Indicator */}
              {lockedLeadsCount > 0 && (
                <Card className="border-2 border-dashed border-orange-300 bg-orange-50/50">
                  <CardContent className="py-8 text-center">
                    <Lock className="h-12 w-12 text-orange-400 mx-auto mb-3" weight="duotone" />
                    <h3 className="font-bold text-lg text-gray-900 mb-1">
                      +{lockedLeadsCount} More Enquiry Locked 🔒
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Premium Plan घ्या आणि सर्व enquiry access करा
                    </p>
                    <Button 
                      onClick={() => navigate('/premium')}
                      className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full"
                    >
                      <Crown className="h-4 w-4 mr-2" weight="fill" /> Unlock All Enquiry
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Premium Plans Section removed - upgrade button already at top */}
      </div>
    </div>
  );
};

export default LeadsPage;
